export type ApprovalRole = 'rrhh' | 'supervisor' | 'manager';

export type CertificateApprovalConfig = {
    requiredApprovals: ApprovalRole[];
    description: string;
};

// Configuración de qué roles deben aprobar cada tipo de certificado
export const CERTIFICATE_APPROVAL_CONFIG: Record<string, CertificateApprovalConfig> = {
    // Certificados que solo necesitan aprobación de RRHH
    standard: {
        requiredApprovals: ['rrhh',],
        description: 'Solo requiere aprobación de RRHH'
    },
    with_salary: {
        requiredApprovals: ['rrhh', 'manager'],
        description: 'requiere aprobación de RRHH, manager'
    },
    job_functions: {
        requiredApprovals: ['supervisor','rrhh','manager'],
        description: 'requiere aprobación de supervisor, RRHH, manager'
    },
    
    
    // Ejemplo de un certificado que necesita 3 aprobaciones (comentado para futuro uso)
    // special_certificate: {
    //     requiredApprovals: ['rrhh', 'supervisor', 'manager'],
    //     description: 'Requiere aprobación de RRHH, Supervisor y Gerencia'
    // },
    
    // Ejemplo de un certificado que necesita 2 aprobaciones (comentado para futuro uso)
    // confidential: {
    //     requiredApprovals: ['rrhh', 'manager'],
    //     description: 'Requiere aprobación de RRHH y Gerencia'
    // }
};

// Función auxiliar para obtener la configuración de un tipo de certificado
export const getApprovalConfig = (certificateType: string): CertificateApprovalConfig => {
    return CERTIFICATE_APPROVAL_CONFIG[certificateType] || {
        requiredApprovals: ['rrhh', 'supervisor', 'manager'], // Por defecto, requiere todas
        description: 'Requiere todas las aprobaciones'
    };
};

// Función para verificar si un certificado está aprobado según su configuración
export const isCertificateApproved = (
    certificateType: string,
    statusByRrhh: string,
    statusBySupervisor: string,
    statusByManager: string
): boolean => {
    const config = getApprovalConfig(certificateType);
    const requiredApprovals = config.requiredApprovals;

    // Mapeo de roles a sus estados
    const statusMap: Record<ApprovalRole, string> = {
        rrhh: statusByRrhh,
        supervisor: statusBySupervisor,
        manager: statusByManager
    };

    // Verificar que todos los roles requeridos hayan aprobado
    return requiredApprovals.every(role => statusMap[role] === 'approved');
};

// Función para verificar si un certificado está rechazado
export const isCertificateRejected = (
    certificateType: string,
    statusByRrhh: string,
    statusBySupervisor: string,
    statusByManager: string
): boolean => {
    const config = getApprovalConfig(certificateType);
    const requiredApprovals = config.requiredApprovals;

    // Mapeo de roles a sus estados
    const statusMap: Record<ApprovalRole, string> = {
        rrhh: statusByRrhh,
        supervisor: statusBySupervisor,
        manager: statusByManager
    };

    // Si alguno de los roles REQUERIDOS rechazó, el certificado está rechazado
    return requiredApprovals.some(role => statusMap[role] === 'rejected');
};

// Función para obtener los pasos de aprobación pendientes
export const getPendingApprovals = (
    certificateType: string,
    statusByRrhh: string,
    statusBySupervisor: string,
    statusByManager: string
): ApprovalRole[] => {
    const config = getApprovalConfig(certificateType);
    const requiredApprovals = config.requiredApprovals;

    const statusMap: Record<ApprovalRole, string> = {
        rrhh: statusByRrhh,
        supervisor: statusBySupervisor,
        manager: statusByManager
    };

    // Retornar los roles que aún están pendientes
    return requiredApprovals.filter(role => 
        statusMap[role] !== 'approved' && statusMap[role] !== 'rejected'
    );
};